import { cleanPermalink } from './top-videos';
import ExcelJS from 'exceljs';
import { format } from 'date-fns';

import type { ReportParams } from './export-account-report-sheets';

const HEADERS = [
  '№',
  'Ссылка на публикацию',
  'Язык публикации',
  'Дата и время размещения публикации',
  'Тема',
  'Статистика просмотров',
  'Скрины',
] as const;

// Column widths in Excel character units; screenshot column wide enough for the image.
const COLUMN_WIDTHS = [6, 44, 16, 24, 44, 16, 32];
const BASE_ROW_HEIGHT = 24; // points; rows without an image
const IMAGE_COL_INDEX = 7; // 1-based: "Скрины"
// Max bounding box for the embedded screenshot, in pixels. Tall box so vertical
// 9:16 reel frames keep their aspect ratio instead of being squished to a square.
const IMAGE_BOX = { width: 200, height: 280 };
const PX_PER_POINT = 4 / 3; // Excel row height is in points; 1pt ≈ 1.333px

// Fit (w,h) into IMAGE_BOX preserving aspect ratio. Never upscales past the box.
function fitImage(nw: number, nh: number): { width: number; height: number } {
  if (nw <= 0 || nh <= 0) return { ...IMAGE_BOX };
  const scale = Math.min(IMAGE_BOX.width / nw, IMAGE_BOX.height / nh);
  return { width: Math.round(nw * scale), height: Math.round(nh * scale) };
}

// Row 0: title. Row 1: column headers. Rows 2+: one publication each.
// Screenshot cell stays empty here — the image is embedded separately.
export function buildReportAoa(params: ReportParams): (string | number)[][] {
  const title = `${params.accountTitle} ${params.period}`.trim();
  const dataRows = params.rows.map((row, i) => [
    i + 1,
    cleanPermalink(row.permalink),
    row.language,
    row.publishedAt ? format(new Date(row.publishedAt), 'dd.MM.yyyy HH:mm') : '',
    row.theme,
    row.views,
    '',
  ]);
  return [[title], [...HEADERS], ...dataRows];
}

function extensionFromUrl(url: string): 'png' | 'jpeg' {
  return /\.png(\?|$)/i.test(url) ? 'png' : 'jpeg';
}

function triggerDownload(buffer: ArrayBuffer, filename: string): void {
  const blob = new Blob([buffer], {
    type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}

export async function generateAccountReportXlsx(params: ReportParams): Promise<void> {
  if (params.rows.length === 0) throw new Error('Нет данных для экспорта');

  const wb = new ExcelJS.Workbook();
  const ws = wb.addWorksheet('Отчёт');

  buildReportAoa(params).forEach((row) => ws.addRow(row));

  COLUMN_WIDTHS.forEach((w, i) => {
    ws.getColumn(i + 1).width = w;
  });
  ws.getRow(2).font = { bold: true };

  // Data rows start at worksheet row 3 (after title row 1 + header row 2).
  for (let i = 0; i < params.rows.length; i++) {
    const row = params.rows[i];
    const sheetRow = i + 3;
    ws.getRow(sheetRow).height = BASE_ROW_HEIGHT;

    const linkCell = ws.getCell(sheetRow, 2);
    linkCell.value = { text: cleanPermalink(row.permalink), hyperlink: cleanPermalink(row.permalink) };
    linkCell.font = { color: { argb: 'FF1155CC' }, underline: true };

    if (!row.imageUrl) continue;
    try {
      const res = await fetch(row.imageUrl);
      if (!res.ok) continue;
      const buffer = await res.arrayBuffer();
      const blob = new Blob([buffer], { type: res.headers.get('content-type') ?? 'image/jpeg' });
      const bitmap = await createImageBitmap(blob);
      const ext = fitImage(bitmap.width, bitmap.height);
      bitmap.close();

      // Grow the row so the (possibly tall) image fits without overlapping.
      ws.getRow(sheetRow).height = Math.max(BASE_ROW_HEIGHT, ext.height / PX_PER_POINT);

      const imageId = wb.addImage({ buffer, extension: extensionFromUrl(row.imageUrl) });
      ws.addImage(imageId, {
        tl: { col: IMAGE_COL_INDEX - 1, row: sheetRow - 1 },
        ext,
        editAs: 'oneCell',
      });
    } catch {
      // image fetch/decode failed — leave the cell empty, keep the row
    }
  }

  const buffer = await wb.xlsx.writeBuffer();
  const filename = `${params.accountTitle} ${params.period}`.trim().replace(/[\\/:*?"<>|]/g, '_');
  triggerDownload(buffer as ArrayBuffer, `${filename}.xlsx`);
}
